/**
 * Human-readable labels for MEAS__* keys — prefer DB meas_display_names via map from CategoriesContext.
 */
import {
  buildMeasDisplayNameMap,
  type CategoryDef,
} from '../helpers/api/config/config';

let cachedDisplayMap: Record<string, string> = {};

export function setMeasurementDisplayMap(map: Record<string, string>): void {
  cachedDisplayMap = map;
}

export function refreshMeasurementDisplayMapFromCategories(
  categories: CategoryDef[]
): void {
  cachedDisplayMap = buildMeasDisplayNameMap(categories);
}

/**
 * Dimensionless measurements: a ratio, not a length. They must never be scaled by pixel
 * spacing and must never be rendered with cm/px. Mirrors `RATIO_MEAS_KEYS` in the inference
 * services (derived there from `"value_type": "ratio"` in the DS measurement catalog), so a
 * new ratio measurement needs one entry here to display correctly.
 */
const RATIO_MEAS_KEYS = new Set<string>(['MEAS__CTR']);

/**
 * Full `MEAS__*` form of a key. The realtime state provider stores measurements with the
 * prefix stripped (`CTR`, `LV_DIAMETER`), while everywhere else keeps it — normalizing here
 * lets both call sites share the label/ratio/allowlist helpers.
 */
export function normalizeMeasKey(measName: string): string {
  const key = (measName || '').split(/\s+/)[0];
  if (!key) return key;
  return key.startsWith('MEAS__') ? key : `MEAS__${key}`;
}

export function isRatioMeasurement(measName: string): boolean {
  return RATIO_MEAS_KEYS.has(normalizeMeasKey(measName));
}

export function getMeasurementDisplayName(measName: string): string {
  if (measName.startsWith('meas_')) {
    const withoutPrefix = measName.replace(/^meas_/, '').replace(/_/g, ' ');
    return withoutPrefix.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  const key = normalizeMeasKey(measName);
  if (cachedDisplayMap[key]) return cachedDisplayMap[key];
  return key.replace(/^MEAS__/, '').replace(/_/g, ' ');
}

export {
  getAllowedMeasurementKeys,
  isMeasurementAllowed,
  measAllowed,
} from '../../../../ui/src/components/MeasurementTable/Components/Report/measurementAllowlist';
